import { useEffect, useRef, useState } from "react";
import { useEventCallback } from "usehooks-ts";

export interface ExpiringItem<T> {
	id: number;
	value: T;
	/** Milliseconds still left (`null` = sticky / no timer, `0` = expired) */
	remaining: number | null;
	ttl: number | null;
}

export function useExpiringItems<T>() {
	const [items, setItems] = useState<ExpiringItem<T>[]>([]);
	const tickRef = useRef(Date.now());
	const idRef = useRef(0); // simple id generator

	/* ─────────────────────────  MAIN TICK  ───────────────────────── */
	useEffect(() => {
		const interval = setInterval(() => {
			setItems((prev) => {
				const now = Date.now();
				const dt = now - tickRef.current;
				tickRef.current = now;

				return prev
					.map((item) => {
						if (item.remaining === null) return item; // sticky
						const next = Math.max(0, item.remaining - dt);
						return { ...item, remaining: next };
					})
					.filter((i) => i.remaining !== 0); // drop the expired ones
			});
		}, 10);

		return () => clearInterval(interval);
	}, []);

	/* ────────────────────  ITEM MANIPULATION  ──────────────────── */
	const addItem = useEventCallback((value: T, opts: { ttl: number | null }) => {
		idRef.current += 1;
		setItems((prev) => [
			...prev,
			{
				id: idRef.current,
				value,
				ttl: opts.ttl,
				remaining: opts.ttl,
			},
		]);
	});

	const pauseItem = useEventCallback((id: number) => {
		setItems((prev) =>
			prev.map((it) =>
				it.id === id && it.remaining !== null
					? { ...it, _pausedSnapshot: it.remaining, remaining: null }
					: it,
			),
		);
	});

	const resumeItem = useEventCallback((id: number) => {
		setItems((prev) =>
			prev.map((it) =>
				it.id === id && it.remaining === null
					? {
							...it,
							remaining: it.ttl,
						}
					: it,
			),
		);
	});

	const removeItem = useEventCallback((id: number) => {
		setItems((prev) => prev.filter((x) => x.id !== id));
	});

	return { items, addItem, pauseItem, resumeItem, removeItem };
}
